import { debounce, find, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { SearchIcon } from 'tdesign-icons-react';
import { Button, DialogPlugin, Dropdown, Input, Link, MessagePlugin } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { fetchVariableGroupList } from '@/slices/variableSlice';
import SvgIcon from '@/components/SvgIcon';
import { isVoid, sleep } from '@/utils/common';
import { deleteVariableGroup } from '@/services/variableService';
import PermWrapper from '@/components/PermWrapper';
import BuriedPermWrapper from '@/components/BuriedWrapper/BuriedPermWrapper';
import Modal from './Modal';

const options = [
  {
    value: 'edit',
    content: <Link hover='color'>修改</Link>,
  },
  {
    value: 'delete',
    content: (
      <Link theme='danger' hover='color'>
        删除
      </Link>
    ),
  },
];

function Group({ fetchVariableGroupList, groups, onSelect, group, scopeType }) {
  const [detail, setDetail] = useState(null);
  const [visible, setVisible] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const EVENTS = useMemo(() => {
    switch (scopeType) {
      // 主机变量
      case 0:
        return {
          CREATE_GROUP: 'TEMPLATE_DETAIL_VARIABLE_HOST_CREATE_GROUP', // 新增变量组
          GROUP_SUBMIT: 'TEMPLATE_DETAIL_VARIABLE_HOST_CREATE_GROUP_SUBMIT', // 新增变量组确认
        };
      // 容器变量
      case 1:
        return {
          CREATE_GROUP: 'TEMPLATE_DETAIL_VARIABLE_CONTAINER_CREATE_GROUP',
          GROUP_SUBMIT: 'TEMPLATE_DETAIL_VARIABLE_CONTAINER_CREATE_GROUP_SUBMIT',
        };
      default:
        return;
    }
  }, [scopeType]);

  const loadData = useMemoizedFn(() => {
    fetchVariableGroupList({
      groupName: searchInput,
      scopeType,
    });
  });

  const setFilter = debounce(async (value) => {
    setSearchInput(value);
    await sleep(10);
    loadData();
  }, 300);

  useEffect(() => {
    loadData();
  }, [loadData, scopeType]);

  useEffect(() => {
    const currentGroupId = group?.id;
    const hasGroups = !isEmpty(groups);
    // 当前没选中，且有分组数据，默认选中第一个
    if (hasGroups && isVoid(currentGroupId)) {
      onSelect(groups[0]);
      return;
    }
    // 如果有分组数据，当时当前节点已经不存在（如删除），默认选中第一个
    if (hasGroups && isEmpty(find(groups, { id: currentGroupId }))) {
      onSelect(groups[0]);
      return;
    }

    // 如删光
    if (!hasGroups) {
      onSelect(null);
    }
  }, [groups, group, onSelect]);

  const onDropDownSelect = (value, row, e) => {
    e.stopPropagation();
    switch (value) {
      case 'delete': // 删除
        handleDel(row);
        break;
      case 'edit': // 修改
        setDetail(row);
        setVisible(true);
        break;
      default:
        break;
    }
  };

  const handleDel = async (row) => {
    const dialog = DialogPlugin.confirm({
      header: '提示',
      theme: 'warning',
      body: '确定删除？',
      confirmBtn: {
        content: '确定',
      },
      onClose: () => dialog.hide(),
      onConfirm: async () => {
        try {
          dialog.update({
            confirmBtn: { loading: true, content: '确定' },
          });
          await deleteVariableGroup(row.id);
          MessagePlugin.success('操作成功！');
          dialog.hide();
          loadData();
        } catch (error) {
          console.trace(error);
          throw error;
        } finally {
          dialog.update({
            confirmBtn: { content: '确定' },
          });
        }
      },
    });
  };

  const addNew = () => {
    setVisible(true);
  };

  const handleClose = (refresh) => {
    setDetail(null);
    setVisible(false);
    if (refresh) {
      loadData();
    }
  };

  return (
    <Root>
      <Title>
        变量组{' '}
        <BuriedPermWrapper
          resource='pulsar:stencil:variables:variablesGroupCreate'
          eventId={EVENTS.CREATE_GROUP}
        >
          <Button size='small' onClick={addNew}>
            新增
          </Button>
        </BuriedPermWrapper>
      </Title>
      <Input
        style={{ margin: '0 20px 12px', width: 'auto' }}
        onChange={setFilter}
        placeholder='请输入变量组'
        clearable
        suffixIcon={<SearchIcon />}
      />
      <Content className='narrow-scrollbar'>
        {groups.map((item) => (
          <Item onClick={() => onSelect(item)} active={group?.id === item.id} key={item.id}>
            <Label>{item.groupName}</Label>
            <PermWrapper resource='pulsar:stencil:variables:variablesGroupConfig'>
              <Dropdown
                options={options}
                onClick={({ value }, e) => onDropDownSelect(value, item, e)}
              >
                <div style={{ padding: '0 6px' }}>
                  <Icon type='dots-vertical' />
                </div>
              </Dropdown>
            </PermWrapper>
          </Item>
        ))}
      </Content>
      <Modal
        scopeType={scopeType}
        visible={visible}
        detail={detail}
        handleClose={handleClose}
        eventId={EVENTS.GROUP_SUBMIT}
      />
    </Root>
  );
}

export default connect(
  (state) => ({
    groups: state.variable.groups,
  }),
  {
    fetchVariableGroupList,
  },
)(Group);

const Root = styled.div`
  width: 220px;
  padding: 24px 0 24px 1px;
  border-right: 2px solid #edeff2;
`;

const Title = styled.div`
  color: var(--td-font-gray-1);
  font-size: 14px;
  padding: 0 20px;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Content = styled.div`
  /* height: calc(100vh - 276px);
  overflow-y: auto; */
`;

const Item = styled.div`
  height: 33px;
  line-height: 33px;
  font-size: 13px;
  padding: 0 10px 0 24px;
  cursor: pointer;
  position: relative;
  display: flex;
  ${(props) =>
    props.active &&
    ` 
      background: #F7F8FA;
      color: var(--td-brand-color-7);
      font-weight: bold;
      padding-right: 8px;
      border-right: 2px solid var(--td-brand-color-7);
    `}
`;

const Label = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
